/**
 * Регистрация, вход и подтверждение адреса.
 *
 * Шаги 11–12 сценария приёмки (ТЗ §16.3): пользователь регистрируется,
 * переносит прогресс, получает trial и может явно согласиться на участие
 * в рейтинге. Между регистрацией и переносом стоит письмо: пока адрес не
 * подтверждён, аккаунта у прогресса нет. Форма одна на оба намерения —
 * поля у входа и регистрации совпадают, различается действие.
 *
 * Состоявшийся аккаунт живёт отдельным файлом: у него своя разбивка на блоки
 * (ТЗ UI-58), и вместе с формой он не помещался в один читаемый экран кода.
 */

import { useEffect, useState } from 'react';
import type { Translate } from '@type2x/i18n';
import { Button, Card } from '@type2x/ui';
import {
  RegistrationError,
  type RegistrationOutcome,
  type RegistrationProblem,
  type SignedIn,
} from '@type2x/feature-accounts';

import type { VerificationState } from '../state/verification.js';
import { AccountProblem } from './AccountProblem.js';
import { AccountSettled } from './AccountSettled.js';
import { ConsentToggle } from './ConsentToggle.js';

/** Итог, который экран показывает после успешного действия. */
type AccountOutcome = RegistrationOutcome | SignedIn;

export interface AccountScreenProps {
  readonly t: Translate;
  readonly onSubmit: (email: string, password: string) => Promise<RegistrationOutcome>;
  readonly onLogin: (email: string, password: string) => Promise<SignedIn>;
  readonly onResend: (email: string) => Promise<void>;
  readonly onForgotPassword: (email: string) => Promise<void>;
  readonly onExport: () => Promise<void>;
  readonly onDelete: (password: string) => Promise<void>;
  /** Выход из аккаунта; `pending` — очередь занятий не разобрана. */
  readonly onLogout: (force?: boolean) => Promise<'done' | 'pending'>;
  readonly onConsentChange: (optIn: boolean) => void;
  readonly consent: boolean;
  /**
   * Спрашивает сервер, кто вошёл. Экран знал состояние аккаунта только как
   * итог последнего действия в этой вкладке: после перезагрузки он снова
   * показывал пустую форму, будто регистрации не было.
   */
  readonly readSession: () => Promise<SignedIn | null>;
  /** Подтверждение по ссылке из письма, если приложение открыто по ней. */
  readonly verification: VerificationState;
  readonly onContinue: () => void;
}

/**
 * Состояние аккаунта по ответу сервера.
 *
 * Вопрос задаётся один раз при открытии экрана: сеть здесь не в пути нажатия,
 * а ответ нужен до первого действия человека. Пока ответа нет — `null`, и
 * экран показывает форму: обещать «вы вошли» до ответа он не вправе.
 */
function useServerSession(readSession: () => Promise<SignedIn | null>): SignedIn | null {
  const [session, setSession] = useState<SignedIn | null>(null);

  useEffect(() => {
    let cancelled = false;
    void readSession().then((current) => {
      if (!cancelled) setSession(current);
    });

    return () => {
      cancelled = true;
    };
  }, [readSession]);

  return session;
}

export function AccountScreen(props: AccountScreenProps): React.JSX.Element {
  const { t, onSubmit, onLogin, onResend, onForgotPassword, onConsentChange } = props;
  const { consent, verification, onContinue, onExport, onDelete, readSession } = props;
  const { onLogout } = props;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<RegistrationProblem | null>(null);
  const [outcome, setOutcome] = useState<AccountOutcome | null>(null);
  const [resent, setResent] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);
  const [busy, setBusy] = useState(false);
  const session = useServerSession(readSession);
  /*
   * Вышли ли из аккаунта в этом показе экрана.
   *
   * Прочитанная у сервера сессия и итог входа живут здесь в памяти, и после
   * выхода оба говорили бы неправду: экран показывал бы карточку аккаунта,
   * которого в этом браузере больше нет.
   */
  const [signedOut, setSignedOut] = useState(false);

  /** Выполняет действие, показывая пользователю причину отказа, но не детали. */
  async function attempt(action: () => Promise<void>): Promise<void> {
    setProblem(null);
    setBusy(true);
    try {
      await action();
    } catch (error) {
      setProblem(error instanceof RegistrationError ? error.problem : 'server-error');
    } finally {
      setBusy(false);
    }
  }

  const submit = (): Promise<void> =>
    attempt(async () => {
      setOutcome(await onSubmit(email, password));
    });

  const login = (): Promise<void> =>
    attempt(async () => {
      setOutcome(await onLogin(email, password));
    });

  const resend = (): Promise<void> =>
    attempt(async () => {
      setResent(false);
      await onResend(email);
      setResent(true);
    });

  const forgotPassword = (): Promise<void> =>
    attempt(async () => {
      setResetRequested(false);
      await onForgotPassword(email);
      setResetRequested(true);
    });

  if (verification.status === 'checking') {
    return (
      <Card title={t('account.title')}>
        <p>{t('account.verifying')}</p>
      </Card>
    );
  }

  // Итог действия важнее прочитанного состояния: он свежее и говорит о том,
  // что человек только что сделал.
  const settled = signedOut
    ? null
    : verification.status === 'done'
      ? verification.outcome
      : (outcome ?? session);

  if (settled !== null && settled.kind !== 'awaiting-verification') {
    return (
      <AccountSettled
        t={t}
        outcome={settled}
        consent={consent}
        busy={busy}
        resent={resent}
        problem={problem}
        onConsentChange={onConsentChange}
        onResend={() => void resend()}
        onContinue={onContinue}
        onExport={onExport}
        onDelete={onDelete}
        onLogout={async (force) => {
          const result = await onLogout(force);
          // Вышли — экран возвращается к форме входа: прочитанная сессия
          // осталась в памяти этого компонента и говорила бы неправду.
          if (result === 'done') setSignedOut(true);
          return result;
        }}
      />
    );
  }

  if (settled !== null) {
    return (
      <Card title={t('account.title')}>
        <p>{t('account.awaiting')}</p>
        {email !== '' && <p>{t('account.awaitingAddress', { email })}</p>}
        <p className="t2x-muted">{t('account.awaitingHint')}</p>
        {resent && <p className="t2x-muted">{t('account.resent')}</p>}
        {problem !== null && <AccountProblem t={t} problem={problem} />}
        <Button onClick={() => void resend()} disabled={busy}>
          {t('account.resend')}
        </Button>
        <ConsentToggle t={t} consent={consent} onConsentChange={onConsentChange} />
      </Card>
    );
  }

  return (
    <Card title={t('account.title')}>
      <p className="t2x-muted">{t('account.explain')}</p>

      {verification.status === 'failed' && (
        <p role="alert" className="t2x-error">
          {t('account.verifyFailed')}
        </p>
      )}

      <label className="t2x-field">
        <span>{t('account.email')}</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>

      <label className="t2x-field">
        <span>{t('account.password')}</span>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>

      {problem !== null && <AccountProblem t={t} problem={problem} />}
      {resent && <p className="t2x-muted">{t('account.resent')}</p>}
      {resetRequested && <p className="t2x-muted">{t('account.resetRequested')}</p>}

      <Button onClick={() => void submit()} disabled={busy}>
        {t('account.submit')}
      </Button>

      {/* Тот же адрес и пароль, другое намерение: заводить второй аккаунт
          человеку с аккаунтом незачем. Вход вторичен по оформлению, но не по
          важности: основное действие на экране одно (ТЗ UI-07). */}
      <p className="t2x-muted">{t('account.loginHint')}</p>
      <Button variant="secondary" onClick={() => void login()} disabled={busy}>
        {t('account.login')}
      </Button>

      {/* Забывший пароль иначе теряет аккаунт вместе с перенесённым в него
          прогрессом: адрес для письма он уже ввёл. */}
      <Button variant="ghost" onClick={() => void forgotPassword()} disabled={busy}>
        {t('account.forgot')}
      </Button>

      {/* Ссылка могла устареть, а письмо — потеряться: попросить новое можно
          и не начиная регистрацию заново. */}
      {verification.status === 'failed' && (
        <Button variant="secondary" onClick={() => void resend()} disabled={busy}>
          {t('account.resend')}
        </Button>
      )}

      <ConsentToggle t={t} consent={consent} onConsentChange={onConsentChange} />
      <LegalLinks t={t} />
    </Card>
  );
}

/**
 * Согласие с документами и ссылки на них.
 *
 * Ссылки стоят рядом с кнопкой регистрации, а не в подвале: соглашаются здесь,
 * и прочитать, с чем именно, человек должен до нажатия, а не после (ТЗ SEC-06).
 * Документы — обычные страницы, отдаваемые тем же сервером: открывать
 * юридический текст поверх тренировки незачем.
 */
function LegalLinks({ t }: { readonly t: Translate }): React.JSX.Element {
  return (
    <p className="t2x-muted t2x-legal">
      {t('legal.agreement')}{' '}
      <a href="/legal/terms.html" target="_blank" rel="noreferrer">
        {t('legal.terms')}
      </a>
      {' · '}
      <a href="/legal/privacy.html" target="_blank" rel="noreferrer">
        {t('legal.privacy')}
      </a>
    </p>
  );
}
